import * as React from "react";
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { Viewer } from "./js/main";
import { sendCustomEvent } from "../events";
import { useEffect, useState } from "react";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {
  TagCreateDialog,
  TagDeleteDialog,
  TagEditDialog
} from "../folder";
import { isStaticSite } from "../electron-modules";
import { DialogContent } from "@material-ui/core";
import {
  EnhancedTable,
  DraggableDialog
} from "../components";
import clsx from 'clsx';
import { nanoid } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";

export interface LabelContextData {
  labelID: number
  pointID: number
  coordinate: number[] | object
}

export interface ContextData {
  sceneCoord?: number[]
  aerialCoord?: number[]
  labelData?: LabelContextData
  selectedTags?: string[]
  modelData?: object[]
}

interface ContextMenuItem {
  text: string
  seperator?: boolean
  disabled?: boolean
  visible?: boolean
  callback: Function
}

export const initialContextState = {
  mouseX: null,
  mouseY: null,
  isAerial: false,
  sceneCoord: null,
  aerialCoord: null,
  labelData: null as LabelContextData,
  selectedTags: null,
  modelData: []
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuItem: {
      fontSize: "0.8em",
      padding: theme.spacing(0.5, 1.5)
    },
    menuList: {
      paddingTop: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.75)
    },
    seperator: {
      borderTop: "1px dashed rgba(100, 100, 100, 0.5)",
      margin: theme.spacing(0.5, 1.5),
      padding: theme.spacing(0)
    }
  }),
);

export const ContextMenu = (props) => {
  const viewer = props.viewer as Viewer;

  const classes = useStyles();
  const {t} = useTranslation();

  const [addTagOpen, setAddTagOpen] = useState(false);
  const [modelProperties, setModelProperties] = useState(null);
  const [deleteTagOpen, setDeleteTagOpen] = useState(false);
  const [tagsToDelete, setTagsToDelete] = useState([]);
  const [tagToEdit, setTagToEdit] = useState(null);
  const [contextState, setContextState] = useState({
    ...initialContextState
  });

  const {isAerial, labelData, selectedTags, modelData,
    sceneCoord, aerialCoord} = contextState;

  const onClose = () => {
    setContextState({
      ...contextState,
      mouseX: null,
      mouseY: null
    });

    viewer?.clearCadHighlight();
  };

  const onEnter = () => {
    props.clearTooltipText();
  };

  const listItemSeperator = (visible) => {
    return {
      text: "",
      seperator: true,
      visible
    };
  };

  const getMenuItems = () => {
    let numLabelsSelected = viewer.numLabelsHighlighted();
    let profileActive = viewer.pointProfileActive();

    const labelSelected = labelData
      ? labelData.labelID !== null
      : false;

    const labelPointSelected = labelData
      ? labelData.pointID !== null
      : false;

    const hasLabelCoord = labelData
      ? labelData.coordinate !== null
      : false;

    const numTagsSelected = selectedTags
      ? selectedTags.length
      : 0;

    const hasModelData = modelData
      ? modelData.length > 0
      : false;

    const hasSceneCoord = !!sceneCoord;
    const hasAerialCoord = !!aerialCoord;

    const pointProfileActions = [
      {
        text: t("context-menu.profile"),
        disabled: !hasSceneCoord,
        visible: !isAerial && !profileActive,
        callback: () => {
          viewer.pointProfileStart(sceneCoord);
        }
      },
      {
        text: t("context-menu.profile-end"),
        disabled: !hasSceneCoord,
        visible: !isAerial && profileActive,
        callback: () => {
          viewer.pointProfileEnd();
        }
      },
      {
        text: t("context-menu.profile-cancel"),
        visible: !isAerial && profileActive,
        callback: () => {
          viewer.pointProfileCancel();
        }
      },
    ];

    const tagActions = [
      {
        text: t("context-menu.new-tag"),
        disabled: numTagsSelected > 0,
        visible: !isStaticSite && (hasSceneCoord || hasAerialCoord),
        callback: () => {
          setAddTagOpen(true);
        }
      },
      {
        text: t("context-menu.delete-tags", {
          count: numTagsSelected
        }),
        visible: !isStaticSite && (numTagsSelected > 0),
        callback: () => {
          setDeleteTagOpen(true);
          setTagsToDelete(selectedTags);
        }
      },
      {
        text: t("context-menu.edit-tag"),
        visible: !isStaticSite && (numTagsSelected === 1),
        callback: () => {
          setTagToEdit(selectedTags[0]);
        }
      },
    ];

    const orbitModeActions = [
      {
        text: viewer.orbitState
          ? t("context-menu.disable-3d-mode")
          : t("context-menu.enable-3d-mode"),
        disabled: !(viewer.hasPoints && viewer.cameraSelected),
        visible: !isAerial,
        callback: () => {
          viewer.toggleOrbitState();
        }
      },
      {
        text: t("context-menu.reset-3d-mode"),
        disabled: !viewer.orbitState,
        visible: !isAerial,
        callback: () => {
          viewer.resetCamera();
        }
      },
    ];

    const modelActions = [
      {
        text: t("context-menu.model-properties"),
        visible: hasModelData,
        callback: () => {
          setModelProperties(modelData);
        }
      }
    ];

    const labelActions = [
      {
        text: t("context-menu.delete-label", {
          count: numLabelsSelected
        }),
        disabled: viewer.anyLabelSelected(),
        callback: () => {
          viewer.deleteHighlighted();
        }
      },
      {
        text: t("context-menu.delete-point"),
        disabled: !labelPointSelected,
        callback: () => {
          viewer.deleteLabelPoint(labelData.pointID);
        }
      },
      {
        text: t("context-menu.unselect-label", {
          count: numLabelsSelected
        }),
        disabled: viewer.anyLabelSelected(),
        callback: () => {
          viewer.unHighlightAll();
        }
      },
      {
        text: t("context-menu.insert-point"),
        disabled: !hasLabelCoord || labelPointSelected,
        callback: () => {
          viewer.insertLabelPoint(
            labelData.labelID,
            labelData.coordinate
          );
        }
      },
      {
        text: t("context-menu.edit-point"),
        disabled: !hasLabelCoord || labelPointSelected,
        callback: () => {
          viewer.editLabel(
            labelData.labelID,
            labelData.coordinate
          );
        }
      },
      {
        text: t("context-menu.find-in-list"),
        disabled: !labelSelected,
        callback: () => {
          viewer.moveToLabel(labelData.labelID);
        }
      }
    ];

    if (numLabelsSelected > 0) {
      // Labels are selected
      return [...labelActions] as ContextMenuItem[];
    }

    // Standard context menu
    return [
      ...tagActions,
      listItemSeperator(hasModelData),
      ...modelActions,
      listItemSeperator(!isAerial),
      ...orbitModeActions,
      listItemSeperator(!isAerial),
      ...pointProfileActions
    ] as ContextMenuItem[];
  };

  /** Context menu event listener */
  useEffect(() => {
    const callback = (contextEvent) => {
      const {event, isAerial, contextData} = contextEvent.detail;

      setContextState({
        mouseX: event.clientX - 2,
        mouseY: event.clientY - 4,
        isAerial,
        ...contextData
      });
    };

    document.addEventListener("context-menu", callback);

    return () => {
      document.removeEventListener("context-menu", callback);
    };
  }, []);

  let options = viewer ? getMenuItems() : [];
  options = options.filter(item => {
    return item.hasOwnProperty("visible")
      ? item.visible
      : true;
  });

  // Make sure a seperator isnt the first element
  if ((options.length > 0) && options[0].seperator) {
    options = options.slice(1);
  }

  const contextOpen = (contextState.mouseY !== null) && options.length > 0;

  const anchorPosition = contextOpen
    ? {top: contextState.mouseY, left: contextState.mouseX}
    : undefined;

  return (
    <React.Fragment>
      <Menu
        keepMounted
        MenuListProps={{
          className: classes.menuList
        }}
        open={contextOpen}
        onClose={onClose}
        onEnter={onEnter}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
      >
        {options.map((option, index) => {
          return (
            <MenuItem
              key={nanoid()}
              disabled={option.disabled}
              className={clsx(classes.menuItem, {
                [classes.seperator]: option.visible && option.seperator
              })}
              onClick={() => {
                onClose();
                option.callback();
              }}
            >
              {option.text}
            </MenuItem>
          );
        })}
      </Menu>

      {/* New Tag Dialog */}
      <TagCreateDialog
        open={addTagOpen}
        setOpen={setAddTagOpen}
        coordinate={sceneCoord || aerialCoord}
      />

      {/* Edit Tag Dialog */}
      <TagEditDialog
        tagToEdit={tagToEdit}
        setTagToEdit={setTagToEdit}
      />

      {/* Delete Tag Dialog */}
      <TagDeleteDialog
        open={deleteTagOpen}
        setOpen={setDeleteTagOpen}
        toDelete={tagsToDelete}
      />

      <ModelProperties
        data={modelProperties}
        setData={setModelProperties}
      />

    </React.Fragment>
  );
};

const ModelProperties = (props) => {
  const {data, setData} = props;

  const {t} = useTranslation();

  const open = data ? data.length > 0 : false;

  const columns = [
    {
      id: 'property',
      label: t("model-properties.property")
    },
    {
      id: 'value',
      label: t("model-properties.value"),
      wordBreak: true
    }
  ];

  const rows = [];
  data?.forEach((property, index) => {
    rows.push({
      id: index,
      ...property
    });
  });

  return (
    <DraggableDialog
      variant={'narrow'}
      open={open}
      title={t("model-properties.title")}
      onClose={() => {
        setData(null);
      }}
    >
      <DialogContent>
        <EnhancedTable
          dense
          pageSize={25}
          columns={columns}
          rows={rows}
        />
      </DialogContent>
    </DraggableDialog>
  );
};

export const showContextMenu = (event, isAerial, contextData = {} as ContextData) => {
  event.preventDefault();
  sendCustomEvent("context-menu", {event, isAerial, contextData});
};